import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import NumberFormat from "react-number-format";
import { BiArrowBack } from "react-icons/bi";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";

import {
  getApprovedQoForJr,
  getJrTemplate,
  getJrCqOptions,
  getJrDetail,
  insertJr,
  updateJr,
  submitJr,
  resetSaveStatus,
  clearTemplate,
  jrV2Selector,
} from "./jrV2Slice";
import { Colors } from "../../../assets/Colors";

const ACTIVITY_LABELS = {
  PORT_SERVICES: "Port Services", RENTAL: "Rental",
  WASTE_MANAGEMENT_SERVICES: "Waste Management Services",
  AREA_RENTAL: "Area Rental", MANPOWER: "Manpower", GENERAL_SERVICE: "General Service",
  // Item pendukung QO (tanpa aktivitas) — dikelompokkan per LineGroup.
  EQUIPMENT: "Equipment", LABOUR: "Labour", TRUCK: "Truck Entry", TWS: "Temporary Waste Storage",
};

// Kunci grup di form: aktivitas QO, atau LineGroup untuk item pendukung
// (Equipment/Labour/Truck Entry/TWS tak punya ActivityName).
const groupKeyOf = (l) => l.ActivityName || l.LineGroup || "OTHER";

const PRIORITY_OPTIONS = [
  { value: "P1", label: "P1 - Normal" },
  { value: "P2", label: "P2 - Urgent" },
  { value: "P3", label: "P3 - Critical" },
];

function fmt(n) {
  if (!n && n !== 0) return "0";
  return new Intl.NumberFormat("id-ID").format(n);
}

function ServiceTypeBadge({ type }) {
  if (type === "F")
    return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">TETAP</span>;
  return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-500">VAR</span>;
}

// ── Activity group (checkbox, tanpa nilai/harga) ───────────────────
// UoM tiap baris selalu ditampilkan (sumber & format sama seperti halaman
// Quotation: SatuanNama, fallback SatuanID). Dua baris bisa punya label
// (ItemDisplay/ItemName) sama persis dalam satu aktivitas — mis. dua baris
// "Laundry" dgn kontrak berbeda (lih. komentar GetJrTemplate di backend) —
// kalau UoM-nya juga sama sehingga masih ambigu, rate kontrak ditambahkan
// sbg pembeda lanjutan.
function ActivityGroup({ activityName, lines, onToggleLine, onToggleActivity, readOnly }) {
  const allChecked  = lines.every(l => l._checked);
  const someChecked = lines.some(l => l._checked);

  const labelOf = (l) => l.ItemDisplay || l.ItemName;
  const uomOf = (l) => l.SatuanNama || l.SatuanID;
  const labelUomCounts = lines.reduce((acc, l) => {
    const k = labelOf(l) + "\u0000" + uomOf(l);
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const differentiatorOf = (l) => {
    const uom = uomOf(l);
    const parts = [];
    if (uom) parts.push(uom);
    if (labelUomCounts[labelOf(l) + "\u0000" + uom] > 1 && l.ContractRate) {
      parts.push(`Rp${fmt(l.ContractRate)}`);
    }
    return parts.length ? parts.join(" · ") : null;
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden mb-3">
      {/* Header aktivitas = checkbox induk */}
      <label className={`flex items-center gap-3 px-5 py-3 border-b cursor-pointer select-none ${
        readOnly ? "" : "hover:bg-gray-50"}`}
        style={{ background: Colors.primary + "12" }}>
        <input
          type="checkbox" disabled={readOnly}
          checked={allChecked}
          ref={el => { if (el) el.indeterminate = !allChecked && someChecked; }}
          onChange={() => onToggleActivity(activityName, !allChecked)}
          className="w-4 h-4"
        />
        <span className="font-semibold text-sm" style={{ color: Colors.primary }}>
          {ACTIVITY_LABELS[activityName] ?? activityName}
        </span>
        <span className="ml-auto text-xs text-gray-500">
          {lines.filter(l => l._checked).length}/{lines.length} dipilih
        </span>
      </label>

      {/* Daftar layanan = checkbox anak */}
      <div className="divide-y divide-gray-50">
        {lines.map(line => (
          <label key={line._idx}
            className={`flex items-center gap-3 px-5 py-2.5 cursor-pointer select-none ${
              readOnly ? "" : "hover:bg-orange-50"} ${line._checked ? "bg-orange-50/40" : ""}`}>
            <input
              type="checkbox" disabled={readOnly}
              checked={!!line._checked}
              onChange={() => onToggleLine(line._idx)}
              className="w-4 h-4 ml-6"
            />
            <span className="text-sm text-gray-800">{labelOf(line)}</span>
            {differentiatorOf(line) && (
              <span className="text-xs text-gray-400">({differentiatorOf(line)})</span>
            )}
            <ServiceTypeBadge type={line.ServiceType} />
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
export default function JobRequestFormPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editJrID = location.state?.JrID ?? null;

  const jrV2 = useSelector(jrV2Selector);

  // User login: client (Customer) kembali ke daftar JR mereka di "/",
  // user RKS kembali ke monitoring JR V2.
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isClient = currentUser?.Tipe === "Customer";
  const jrListPath = isClient ? "/" : "/operasional-job-request-list";

  const [form, setForm]         = useState(null);
  const [errors, setErrors]     = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // CQ (turunan customer) — pilihan mengikuti KlienID pemilik QO.
  const [cqOptions, setCqOptions] = useState([]);
  const skipAutoNav = useRef(false); // cegah auto-navigate saat chain save+submit

  // ── Load ──
  useEffect(() => {
    dispatch(getApprovedQoForJr());
    if (editJrID) {
      dispatch(getJrDetail(editJrID));
    } else {
      // form baru
      setForm({
        QoNo: "", WorkDate: new Date().toISOString().slice(0, 10),
        SPKNo: "", WSBNo: "", Priority: "P1",
        RequestUnit: "", PermitToWork: "", Remarks: "",
        CQID: 0,
        Lines: [],
      });
    }
    return () => { dispatch(clearTemplate()); dispatch(resetSaveStatus()); };
  }, []); // eslint-disable-line

  // ── Init form on edit: header dari detail + load template, pre-check line tersimpan ──
  useEffect(() => {
    if (editJrID && jrV2.detail && !form) {
      const d = jrV2.detail;
      // QoLineID yang sudah dipilih di JR ini
      const savedIds = new Set((d.Lines || []).map(l => l.QoLineID).filter(Boolean));
      const jrType = d.JrType || "OPS";
      setForm({
        JrID: d.JrID, QoID: d.QoID, QoNo: d.QoNo, KlienID: d.KlienID,
        CQID: d.CQID || 0, KodeCQ: d.KodeCQ, NamaCQ: d.NamaCQ,
        WorkDate: d.WorkDate, JrType: jrType, Period: d.Period,
        SPKNo: d.SPKNo, WSBNo: d.WSBNo, Priority: d.Priority,
        RequestUnit: d.RequestUnit, PermitToWork: d.PermitToWork, Remarks: d.Remarks,
        Status: d.Status, StatusLabel: d.StatusLabel,
        Lines: [],
      });
      dispatch(getJrTemplate({ qoNo: d.QoNo, jrType })).unwrap().then(res => {
        const lines = (res?.data ?? []).map((l, i) => ({
          ...l, _idx: i,
          _checked: savedIds.has(l.QoLineID),
        }));
        setForm(prev => ({ ...prev, Lines: lines }));
      });
    }
  }, [jrV2.detail]); // eslint-disable-line

  // ── Muat pilihan CQ tiap kali customer (KlienID) berubah ──
  useEffect(() => {
    if (!form?.KlienID) { setCqOptions([]); return; }
    dispatch(getJrCqOptions(form.KlienID)).unwrap()
      .then(res => setCqOptions(res?.data ?? []))
      .catch(() => setCqOptions([]));
  }, [form?.KlienID]); // eslint-disable-line

  // ── Navigate on save success (skip jika sedang chain submit) ──
  useEffect(() => {
    if (jrV2.isSaveSuccess && !skipAutoNav.current) {
      toast.success("Job Request berhasil disimpan");
      dispatch(resetSaveStatus());
      navigate(jrListPath);
    }
  }, [jrV2.isSaveSuccess]); // eslint-disable-line

  // ── QO options ──
  const qoOptions = useMemo(() =>
    (jrV2.approvedQo ?? []).map(q => ({
      value: q.QoNo,
      label: `${q.QoNo} — ${q.NamaKlien}`,
      klien: q.NamaKlien,
      klienID: q.KlienID,
    })), [jrV2.approvedQo]);

  // ── Handle QO selection: load template, Fixed pre-checked ──
  const handleSelectQo = useCallback((qoNo) => {
    // KlienID pemilik QO ikut disimpan ke form — dipakai menentukan siapa yang
    // boleh mengajukan JR ini (lihat isOwnerClient). Saat edit, KlienID sudah
    // datang dari detail JR.
    const klienID = (jrV2.approvedQo ?? []).find(q => q.QoNo === qoNo)?.KlienID ?? "";
    // Ganti QO → CQ lama belum tentu milik customer baru, kosongkan.
    setForm(prev => ({ ...prev, QoNo: qoNo, KlienID: klienID,
      CQID: prev.KlienID === klienID ? prev.CQID : 0 }));
    setErrors(p => ({ ...p, QoNo: undefined }));
    dispatch(getJrTemplate({ qoNo, jrType: "OPS" })).unwrap().then(res => {
      const lines = (res?.data ?? []).map((l, i) => ({
        ...l, _idx: i,
        _checked: l.ServiceType === "F", // layanan tetap dicentang otomatis
      }));
      setForm(prev => ({ ...prev, QoNo: qoNo, KlienID: klienID, Lines: lines }));
    });
  }, [dispatch, jrV2.approvedQo]);

  const updateHeader = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(p => ({ ...p, [field]: undefined }));
  }, []);

  // ── Toggle satu layanan ──
  const toggleLine = useCallback((idx) => {
    setForm(prev => ({
      ...prev,
      Lines: prev.Lines.map(l => l._idx === idx ? { ...l, _checked: !l._checked } : l),
    }));
  }, []);

  // ── Toggle semua layanan dalam satu aktivitas ──
  const toggleActivity = useCallback((activityName, value) => {
    setForm(prev => ({
      ...prev,
      Lines: prev.Lines.map(l =>
        groupKeyOf(l) === activityName ? { ...l, _checked: value } : l),
    }));
  }, []);

  // ── Group lines by activity ──
  const groupedLines = useMemo(() => {
    if (!form?.Lines) return {};
    return form.Lines.reduce((acc, l) => {
      const key = groupKeyOf(l);
      (acc[key] = acc[key] || []).push(l);
      return acc;
    }, {});
  }, [form?.Lines]);

  // Opsi dropdown CQ. CQ tersimpan yang kini nonaktif tetap ditampilkan agar
  // JR lama tidak tampak kosong (backend mengizinkan mempertahankannya).
  const cqSelectOptions = useMemo(() => {
    const opts = cqOptions.map(c => ({
      value: c.CQID,
      label: c.NamaCQ ? `${c.KodeCQ} — ${c.NamaCQ}` : c.KodeCQ,
    }));
    if (form?.CQID && !opts.some(o => o.value === form.CQID)) {
      opts.push({
        value: form.CQID,
        label: `${form.KodeCQ || form.CQID}${form.NamaCQ ? " — " + form.NamaCQ : ""} (nonaktif)`,
      });
    }
    return opts;
  }, [cqOptions, form?.CQID, form?.KodeCQ, form?.NamaCQ]);
  // JR General Service: CQ ditetapkan saat generate, tak bisa diubah di form.
  const cqRequired = form?.JrType !== "GS" && cqOptions.length > 0;

  const checkedCount = useMemo(
    () => (form?.Lines ?? []).filter(l => l._checked).length,
    [form?.Lines]
  );

  const validate = () => {
    const e = {};
    if (!form.QoNo)     e.QoNo = "Pilih QO terlebih dahulu";
    if (!form.WorkDate) e.WorkDate = "Wajib diisi";
    if (cqRequired && !form.CQID) e.CQID = "Pilih CQ";
    if (checkedCount === 0) e.Lines = "Pilih minimal satu layanan";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Hanya layanan yang dicentang yang dikirim; qty diisi RKS belakangan (ActualQty null)
  const buildPayload = () => ({
    ...form,
    Lines: form.Lines
      .filter(l => l._checked)
      .map(({ _idx, _checked, ...l }) => ({ ...l, ActualQty: null })),
  });

  const handleSave = () => {
    if (!validate()) { toast.error("Lengkapi data wajib"); return; }
    const payload = buildPayload();
    if (editJrID) dispatch(updateJr(payload));
    else dispatch(insertJr(payload));
  };

  // Simpan dulu (persist actual qty) → submit → balik ke monitoring
  const handleSubmitJr = async () => {
    if (!validate()) { toast.error("Lengkapi data wajib"); return; }
    setIsSubmitting(true);
    skipAutoNav.current = true; // cegah effect navigate saat updateJr fulfilled
    try {
      const saved = await dispatch(updateJr(buildPayload())).unwrap();
      const jrID  = saved?.data?.JrID || form.JrID;
      await dispatch(submitJr(jrID)).unwrap();
      toast.success(form.JrType === "GS"
        ? "JR General Service disubmit, masuk Menunggu Activity"
        : "Job Request berhasil disubmit ke RKS");
      dispatch(resetSaveStatus());
      navigate(jrListPath);
    } catch (e) {
      toast.error(typeof e === "string" ? e : (e?.message ?? "Gagal submit JR"));
      skipAutoNav.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!form || (editJrID && jrV2.isDetailLoading)) {
    return <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">Memuat data…</div>;
  }

  const isDraft = !editJrID || form.Status === -1;
  // Mengajukan JR (draft & submit) adalah tahap CLIENT: hanya user client
  // pemilik quotation yang boleh. Peran RKS baru mulai setelah JR disubmit
  // (status 99/100) lewat halaman qty/manifest/activity. Untuk user client,
  // "Nik" hasil login memang berisi KlienID (SUser.Nik = MKlien.KlienID).
  // Ini lapisan UI saja — penegakannya ada di backend (requireClientOwner).
  const isOwnerClient = isClient && !!form.KlienID && currentUser?.Nik === form.KlienID;
  const canEdit = isDraft && isOwnerClient;
  const readOnly = !canEdit;
  const isGS = form.JrType === "GS";

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <div className="flex items-center gap-2">
          {form.JrType === "GS" && (
            <span className="px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700">
              General Service{form.Period ? ` · ${form.Period}` : ""}
            </span>
          )}
          {form.StatusLabel && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
              {form.StatusLabel}
            </span>
          )}
          {canEdit && (
            <button onClick={handleSave} disabled={jrV2.isSaving || isSubmitting}
              className="px-5 py-1.5 rounded-lg border text-sm font-semibold"
              style={{ borderColor: Colors.primary, color: Colors.primary }}>
              {jrV2.isSaving && !isSubmitting ? "Menyimpan…" : "Simpan Draft"}
            </button>
          )}
          {canEdit && editJrID && (
            <button onClick={handleSubmitJr} disabled={jrV2.isSaving || isSubmitting}
              className="px-5 py-1.5 rounded-lg text-white text-sm font-semibold bg-green-600 hover:bg-green-700 disabled:opacity-50">
              {isSubmitting ? "Submitting…" : (isGS ? "Submit ke Activity" : "Submit ke RKS")}
            </button>
          )}
        </div>
      </div>

      {/* ── Info banner untuk JR baru (belum bisa submit sebelum disimpan) ── */}
      {canEdit && !editJrID && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
          Simpan JR sebagai draft terlebih dahulu, lalu buka kembali untuk submit ke RKS.
        </div>
      )}

      {/* ── Kenapa tombol aksi tidak muncul: jelaskan, jangan biarkan tampak rusak.
           Client yang belum memilih QO belum bisa dinilai kepemilikannya, jadi
           jangan ditegur duluan — banner hanya muncul setelah QO terpilih. ── */}
      {isDraft && !isOwnerClient && (!isClient || !!form.KlienID) && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
          {isClient
            ? "Job Request ini milik client lain, jadi hanya dapat dilihat."
            : "Job Request hanya dapat diajukan oleh user client pemilik quotation. Halaman ini tampil sebagai lihat-saja."}
        </div>
      )}

      {/* ── Header form ── */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-4">Informasi Job Request</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="md:col-span-2">
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              Quotation / Kontrak <span className="text-red-500">*</span>
            </label>
            <Select
              isDisabled={!!editJrID}
              menuPortalTarget={document.body}
              value={qoOptions.filter(o => o.value === form.QoNo)}
              onChange={e => handleSelectQo(e.value)}
              options={qoOptions}
              placeholder="Pilih QO yang sudah disetujui…"
            />
            {errors.QoNo && <p className="text-red-500 text-xs mt-1">{errors.QoNo}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              Tanggal Pekerjaan <span className="text-red-500">*</span>
            </label>
            <DatePicker dateFormat="dd/MM/yyyy" disabled={!!editJrID}
              className="w-full border rounded px-3 py-2 text-sm outline-none disabled:bg-gray-100"
              selected={form.WorkDate ? new Date(form.WorkDate) : null}
              onChange={d => updateHeader("WorkDate", d?.toISOString().slice(0, 10))} />
            {errors.WorkDate && <p className="text-red-500 text-xs mt-1">{errors.WorkDate}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
              CQ {cqRequired && <span className="text-red-500">*</span>}
            </label>
            <Select menuPortalTarget={document.body}
              isDisabled={readOnly || isGS || !form.KlienID || cqSelectOptions.length === 0}
              isClearable={!cqRequired}
              value={cqSelectOptions.filter(o => o.value === form.CQID)}
              onChange={e => updateHeader("CQID", e?.value ?? 0)}
              options={cqSelectOptions}
              placeholder={!form.KlienID ? "Pilih QO dahulu"
                : cqSelectOptions.length === 0 ? "Customer tidak punya CQ" : "Pilih CQ…"} />
            {errors.CQID && <p className="text-red-500 text-xs mt-1">{errors.CQID}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">SPK No</label>
            <input type="text" value={form.SPKNo ?? ""} disabled={readOnly}
              onChange={e => updateHeader("SPKNo", e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm outline-none disabled:bg-gray-100" />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">WSB No</label>
            <input type="text" value={form.WSBNo ?? ""} disabled={readOnly}
              onChange={e => updateHeader("WSBNo", e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm outline-none disabled:bg-gray-100" />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Prioritas</label>
            <Select menuPortalTarget={document.body} isDisabled={readOnly}
              value={PRIORITY_OPTIONS.filter(o => o.value === form.Priority)}
              onChange={e => updateHeader("Priority", e.value)}
              options={PRIORITY_OPTIONS} />
          </div>
        </div>
      </div>

      {/* ── Lines ── */}
      {!form.QoNo ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          Pilih QO terlebih dahulu untuk menampilkan daftar layanan
        </div>
      ) : jrV2.isTemplateLoading ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">Memuat layanan…</div>
      ) : form.Lines.length === 0 ? (
        <div className="bg-white rounded-xl border p-10 text-center text-gray-400">
          QO ini tidak punya item layanan
        </div>
      ) : (
        <>
          <div className="mb-3 px-1 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-600">
              Pilih layanan yang dikerjakan
            </span>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><ServiceTypeBadge type="F" /> otomatis dari kontrak</span>
              <span className="flex items-center gap-1"><ServiceTypeBadge type="V" /> sesuai kejadian</span>
            </div>
          </div>
          {errors.Lines && <p className="text-red-500 text-xs mb-2 px-1">{errors.Lines}</p>}

          {Object.entries(groupedLines).map(([act, lines]) => (
            <ActivityGroup key={act} activityName={act} lines={lines}
              onToggleLine={toggleLine} onToggleActivity={toggleActivity} readOnly={readOnly} />
          ))}

          <div className="mt-3 px-1 text-xs text-gray-400 italic">
            Kuantitas/jumlah aktual akan diisi oleh RKS pada tahap berikutnya.
          </div>
        </>
      )}
    </div>
  );
}
